import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileText, Loader2, Sparkles, MapPin, Target } from "lucide-react";
import ContatosEditor from "./ContatosEditor";
import { CONSULTOR_NONE, onlyDigits, formatCNPJ, isValidCNPJ } from "./constants";

export default function CondominioFormDialog({ open, setOpen, editing, form, setF, cnpjLoading, cnpjLoadedFor, vendedores, handleSave, addContato, updateContato, removeContato }) {
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dialog-condominio">
        <DialogHeader>
          <DialogTitle>{editing ? "Editar condomínio" : "Novo condomínio"}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
          <div className="sm:col-span-2 space-y-1.5">
            <Label>Condomínio (nome) *</Label>
            <Input data-testid="input-condominio-nome" value={form.condominio} onChange={(e) => setF("condominio", e.target.value)} placeholder="Ex: Condomínio Viva Vista" />
          </div>

          {/* CNPJ + auto-lookup BrasilAPI */}
          <div className="sm:col-span-2 space-y-1.5">
            <div className="flex items-center gap-2 flex-wrap">
              <Label className="flex items-center gap-1.5 mb-0">
                <FileText className="w-3.5 h-3.5 text-muted-foreground" />
                CNPJ do condomínio <span className="text-xs text-muted-foreground font-normal">(opcional)</span>
              </Label>
              {cnpjLoading && (
                <span className="flex items-center gap-1 text-[10px] text-sky-500" data-testid="cnpj-lookup-loading-condo">
                  <Loader2 className="w-3 h-3 animate-spin" /> consultando Receita…
                </span>
              )}
              {!cnpjLoading && cnpjLoadedFor && cnpjLoadedFor === onlyDigits(form.cnpj) && isValidCNPJ(form.cnpj) && (
                <span className="flex items-center gap-1 text-[10px] text-emerald-500">
                  <Sparkles className="w-3 h-3" /> dados carregados
                </span>
              )}
            </div>
            <Input
              data-testid="input-condominio-cnpj"
              value={form.cnpj}
              onChange={(e) => setF("cnpj", formatCNPJ(e.target.value))}
              placeholder="00.000.000/0000-00"
              inputMode="numeric"
              className={
                form.cnpj && onlyDigits(form.cnpj).length === 14 && !isValidCNPJ(form.cnpj)
                  ? "border-red-500/60 focus-visible:ring-red-500/40"
                  : ""
              }
            />
            {form.cnpj && onlyDigits(form.cnpj).length === 14 && !isValidCNPJ(form.cnpj) && (
              <p className="text-[11px] text-red-500" data-testid="cnpj-invalid-condo">CNPJ inválido — verifique os dígitos.</p>
            )}
            {(!form.cnpj || onlyDigits(form.cnpj).length < 14) && (
              <p className="text-[11px] text-muted-foreground">
                Digite os 14 dígitos e a Receita preenche automaticamente razão social e endereço.
              </p>
            )}
          </div>

          <div className="sm:col-span-2 space-y-1.5">
            <Label className="flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5 text-muted-foreground" /> Endereço
            </Label>
            <Input
              data-testid="input-condominio-endereco"
              value={form.endereco}
              onChange={(e) => setF("endereco", e.target.value)}
              placeholder="Ex: Av. Rebouças, 1500 · Pinheiros · São Paulo - SP · CEP 05402-100"
            />
          </div>
          <div className="sm:col-span-2 space-y-1.5">
            <Label>Complemento <span className="text-xs text-muted-foreground font-normal">(bloco, andar, ponto de referência)</span></Label>
            <Input
              data-testid="input-condominio-complemento"
              value={form.complemento}
              onChange={(e) => setF("complemento", e.target.value)}
              placeholder="Ex: Bloco A · Portaria 24h · Acesso pela lateral"
            />
          </div>

          <ContatosEditor form={form} addContato={addContato} updateContato={updateContato} removeContato={removeContato} />

          <div className="space-y-1.5">
            <Label>Tipo</Label>
            <Select value={form.tipo} onValueChange={(v) => setF("tipo", v)}>
              <SelectTrigger data-testid="select-tipo"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="residencial">Residencial</SelectItem>
                <SelectItem value="comercial">Comercial</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Situação da infraestrutura</Label>
            <Select value={form.infra_status} onValueChange={(v) => setF("infra_status", v)}>
              <SelectTrigger data-testid="select-infra-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="atendido">Atendido (já tem infraestrutura)</SelectItem>
                <SelectItem value="prospecto">Prospecto (aguardando aprovação)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Status do contato</Label>
            <Select value={form.status_contato} onValueChange={(v) => setF("status_contato", v)}>
              <SelectTrigger data-testid="select-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="telefone">Telefone</SelectItem>
                <SelectItem value="presencial">Presencial</SelectItem>
                <SelectItem value="agendado">Agendado</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Qtd. Apartamentos/Salas</Label>
            <Input type="number" min="0" value={form.qtd_apartamentos} onChange={(e) => setF("qtd_apartamentos", e.target.value)} />
          </div>
          {form.infra_status === "atendido" && (
            <div className="space-y-1.5">
              <Label>Qtd. Clientes atuais</Label>
              <Input type="number" min="0" value={form.qtd_clientes_atuais} onChange={(e) => setF("qtd_clientes_atuais", e.target.value)} />
            </div>
          )}
          <div className="space-y-1.5">
            <Label>Responsável / Contato local</Label>
            <Input value={form.responsavel} onChange={(e) => setF("responsavel", e.target.value)} placeholder="Ex: Zelador, Síndico, Contato interno" data-testid="input-condominio-responsavel" />
          </div>
          <div className="space-y-1.5">
            <Label>Consultor (vendedor associado)</Label>
            <Select
              value={form.consultor || CONSULTOR_NONE}
              onValueChange={(v) => setF("consultor", v === CONSULTOR_NONE ? "" : v)}
            >
              <SelectTrigger data-testid="select-condominio-consultor">
                <SelectValue placeholder="Nenhum consultor" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={CONSULTOR_NONE}>Nenhum</SelectItem>
                {vendedores.map((v) => (
                  <SelectItem key={v} value={v} data-testid={`option-consultor-${v}`}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {form.infra_status === "atendido" ? (
            <>
              <div className="space-y-1.5">
                <Label>Semana planejada</Label>
                <Input value={form.semana_planejada} onChange={(e) => setF("semana_planejada", e.target.value)} placeholder="Ex: Semana 2 - Março" />
              </div>
              <div className="space-y-1.5">
                <Label>Meta de vendas</Label>
                <Input type="number" min="0" value={form.meta_vendas} onChange={(e) => setF("meta_vendas", e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>Vendas realizadas</Label>
                <Input type="number" min="0" value={form.vendas_realizadas} onChange={(e) => setF("vendas_realizadas", e.target.value)} />
              </div>
              <div className="sm:col-span-2 rounded-lg border border-primary/30 bg-primary/5 p-4 grid grid-cols-2 gap-3">
                <div className="col-span-2 flex items-center gap-2 text-sm font-semibold text-primary">
                  <Target className="w-3.5 h-3.5" /> Metas de clientes deste condomínio
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Meta geral (total do prédio)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={form.meta_clientes_total}
                    onChange={(e) => setF("meta_clientes_total", e.target.value)}
                    placeholder="Ex: 60"
                    data-testid="input-meta-clientes-total"
                  />
                  <p className="text-[10px] text-muted-foreground">Total de moradores/salas que queremos converter em cliente FLUA.</p>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Meta mensal (ritmo)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={form.meta_clientes_mensal}
                    onChange={(e) => setF("meta_clientes_mensal", e.target.value)}
                    placeholder="Ex: 5"
                    data-testid="input-meta-clientes-mensal"
                  />
                  <p className="text-[10px] text-muted-foreground">Quantos clientes novos esperamos vender neste prédio a cada mês.</p>
                </div>
              </div>
            </>
          ) : (
            <div className="space-y-1.5">
              <Label>Aprovação prevista</Label>
              <Input
                type="date"
                value={form.data_aprovacao_prevista}
                onChange={(e) => setF("data_aprovacao_prevista", e.target.value)}
                data-testid="input-data-aprovacao-prevista"
              />
            </div>
          )}
          <div className="sm:col-span-2 space-y-1.5">
            <Label>Observações</Label>
            <Textarea rows={3} value={form.observacoes} onChange={(e) => setF("observacoes", e.target.value)} placeholder="Notas estratégicas…" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
          <Button onClick={handleSave} data-testid="btn-save-condominio">{editing ? "Salvar alterações" : "Cadastrar"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
